import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Sparkles, ChevronRight } from "lucide-react";
import { weekKeyToLabel, weekKeyToDateRange, getISOWeekKey } from "@/lib/week";

export default function SundayResetHistoryPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sunday-reset/history");
        setEntries(data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentWeek = getISOWeekKey(new Date());

  return (
    <div className="min-h-screen">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 lg:p-8 space-y-5">
        <div className="flex items-center gap-2 flex-wrap">
          <MenuSheet />
          <Button variant="ghost" size="sm" asChild className="rounded-xl" data-testid="reset-history-back">
            <Link to="/sunday-reset">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to Sunday Reset
            </Link>
          </Button>
        </div>

        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
            <Sparkles strokeWidth={1.5} className="size-3.5" />
            History
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Your Sunday Resets
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every entry you've saved, newest first.
          </p>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : entries.length === 0 ? (
          <Card className="rounded-2xl">
            <CardContent className="pt-8 pb-8 text-center space-y-3">
              <div className="size-12 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto">
                <Sparkles strokeWidth={1.5} className="size-6 text-primary" />
              </div>
              <p className="text-base font-medium">No resets yet</p>
              <p className="text-sm text-muted-foreground">
                Once you save your first Sunday Reset it will show up here.
              </p>
              <Button asChild className="rounded-xl mt-2" data-testid="reset-history-start">
                <Link to="/sunday-reset">Start this week</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="reset-history-list">
            {entries.map((e) => {
              const summary =
                e.reflect_well ||
                e.reflect_proud ||
                e.plan_excited ||
                (Array.isArray(e.plan_priorities) ? e.plan_priorities.filter(Boolean).join(" · ") : "") ||
                "—";
              return (
                <Link
                  key={e.id}
                  to={`/sunday-reset?week=${encodeURIComponent(e.week_key)}`}
                  className="block group"
                  data-testid={`reset-history-item-${e.week_key}`}
                >
                  <Card className="rounded-2xl transition-colors group-hover:border-primary/40">
                    <CardContent className="pt-5 pb-5 flex items-center gap-4">
                      <div className="size-11 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Sparkles strokeWidth={1.5} className="size-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-display text-lg font-bold tracking-tight">
                            {weekKeyToLabel(e.week_key)}
                          </h3>
                          {e.week_key === currentWeek && (
                            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary bg-primary/10 rounded-full px-2 py-0.5">
                              This week
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {weekKeyToDateRange(e.week_key)}
                        </p>
                        <p className="text-sm text-foreground/80 mt-1 line-clamp-2">{summary}</p>
                      </div>
                      <ChevronRight strokeWidth={1.5} className="size-5 text-muted-foreground shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
